import React from "react";
import { useNavigate } from "react-router-dom";
import { httpClient } from "../../../../infrastructure/api/http-client";
import { NEW_API_ENDPOINTS } from "../../../../shared/constants/api";

interface OnboardingItem {
  key: string;
  title: string;
  description: string;
  /** true: 운영에 꼭 필요 / false: 쓰는 기능에 따라 필요 */
  required: boolean;
  done: boolean;
  /** `/company/{companyId}` 아래 상대 경로 */
  path: string;
}

/**
 * MES 홈 온보딩 — 먼저 등록해야 하는 설정 안내.
 *
 * 등록하지 않아도 화면은 동작하지만 결과가 조용히 빠지는 설정들이다
 * (예: 메일 설정이 없으면 세금증빙 발급 안내 메일이 고객에게 가지 않는다).
 * 남은 항목만 보여 주고, 모두 끝났거나 점검을 불러오지 못하면 영역을 숨긴다.
 */
export const CompanyOnboardingPanel: React.FC<{ companyId: number }> = ({ companyId }) => {
  const navigate = useNavigate();
  const [items, setItems] = React.useState<OnboardingItem[] | null>(null);

  React.useEffect(() => {
    let alive = true;
    httpClient
      .get<{ data: { items: OnboardingItem[] } }>(NEW_API_ENDPOINTS.companyOnboardingController.GET(companyId))
      .then((res) => alive && setItems(res.data.data.items))
      .catch(() => alive && setItems(null));
    return () => {
      alive = false;
    };
  }, [companyId]);

  if (!items) return null;
  const remaining = items.filter((item) => !item.done);
  if (remaining.length === 0) return null;
  // 필수 항목을 먼저
  const ordered = [...remaining].sort((a, b) => Number(b.required) - Number(a.required));
  const doneCount = items.length - remaining.length;

  return (
    // 한 줄 칩 — 설명은 마우스를 올리면 보인다(title). 대시보드 위쪽 공간을 덜 차지하도록
    <section className="bg-white rounded-[12px] border border-[#4285F4]/20 px-4 py-2.5 text-left flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="flex items-center gap-2 shrink-0">
        <h3 className="text-[13px] font-bold text-[#222]">시작하기 전에 등록해 주세요</h3>
        <span className="text-[12px] text-[#888] tabular-nums">
          {doneCount}/{items.length}
        </span>
      </div>
      <ul className="flex flex-wrap items-center gap-1.5 min-w-0">
        {ordered.map((item) => (
          <li key={item.key}>
            <button
              onClick={() => navigate(`/company/${companyId}/${item.path}`)}
              title={item.description}
              className={`inline-flex items-center gap-1 h-[28px] pl-2 pr-2.5 rounded-full border text-[12px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4285F4] ${
                item.required
                  ? "border-[#4285F4]/30 bg-[#F5F8FF] text-[#3367D6] hover:bg-[#E8EFFF]"
                  : "border-[#E5E5E5] bg-white text-[#555] hover:bg-[#FAFAFA]"
              }`}
            >
              <span className={`text-[10px] font-semibold ${item.required ? "text-[#4285F4]" : "text-[#999]"}`}>
                {item.required ? "필수" : "권장"}
              </span>
              <span className="font-medium">{item.title}</span>
              <span aria-hidden className="text-[#AAAAAA]">›</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};
